import React, { useState } from 'react';
import { useCalculator } from '../context/CalculatorContext';
import { formatCurrency, formatPercent } from '../utils/formatters';
import AnimatedNumberDisplay from './ui/AnimatedNumberDisplay';
import { TrendingUp, DollarSign, Target, ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight, Activity, RefreshCw, Zap, Shield, Award } from 'lucide-react';
import { motion } from 'framer-motion';
import { getCertificatePhasesSummary } from '../utils/calculations/certificateEvolution';
import { getDetailedCertificateEvolution } from '../utils/calculations/certificateEvolution';

const ResultsSummary: React.FC = () => {
  const { investment, results, patrimonyChartVisibility } = useCalculator();
  const [showPerCertificate, setShowPerCertificate] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>(['summary', 'comparison']);

  if (!results) {
    return (
      <div className="flex items-center justify-center p-8 bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Calculando resultados...</p>
          <p className="text-gray-500 text-sm mt-2">Por favor espera mientras procesamos los datos</p>
        </div>
      </div>
    );
  }

  // Get detailed certificate evolution and phases
  const certificateEvolution = getDetailedCertificateEvolution(investment);
  const phases = getCertificatePhasesSummary(certificateEvolution, investment.years);

  const { 
    finalMonthlyIncome, 
    finalPatrimony, 
    capitalMultiplier, 
    cagr, 
    certificatesSummary,
    passiveIncomeEfficiency,
    paybackYear,
    irr,
  } = results;

  // Calculate per certificate values
  const totalCertificates = certificatesSummary.totalCertificates;
  const perCertificateValues = {
    monthlyIncome: finalMonthlyIncome / totalCertificates,
    patrimony: finalPatrimony / totalCertificates,
  };

  // Get the last year data for cash out summary
  const lastYearData = results.yearlyData[results.yearlyData.length - 1];
  const monthlyPartialCashOut = investment.partialCashOut ? 
    (lastYearData.partialCashOutAmount || 0) / 12 : 0;

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      if (prev.includes(section)) {
        return prev.filter(s => s !== section);
      } else {
        return [...prev, section];
      }
    });
  };

  return (
    <div className="space-y-6">
      {/* Expandable Sections */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('summary')}
          className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white hover:from-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Activity className="w-6 h-6 text-green-600" />
            <h3 className="font-medium text-gray-800">Métricas Detalladas</h3>
          </div>
          {expandedSections.includes('summary') ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            expandedSections.includes('summary') ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="p-6 space-y-8">
            {/* Returns */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign className="w-5 h-5 text-green-600" />
                <h4 className="text-lg font-medium text-gray-800">Rendimientos</h4>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-green-50 to-teal-50 p-6 rounded-xl shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <h4 className="font-medium text-green-800">Ingreso Mensual</h4>
                  </div>
                  
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold text-green-800">
                      <AnimatedNumberDisplay
                        value={showPerCertificate ? perCertificateValues.monthlyIncome : finalMonthlyIncome}
                        currencyFormat={investment.currencyFormat}
                        duration={1.2}
                        staggerDelay={0.08}
                      />
                    </div>
                    <span className="text-sm text-green-600">{showPerCertificate ? '/certificado' : 'total'}</span>
                  </div>
                  
                  <div className="mt-2">
                    <div className="h-2 bg-green-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 rounded-full transition-all duration-500"
                        style={{ width: `${(finalMonthlyIncome / (finalPatrimony * 0.02)) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  {investment.partialCashOut && (
                    <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-amber-700">Retiro Parcial ({investment.cashOutPercentage}%):</span>
                        <span className="font-medium text-amber-700">
                          {formatCurrency(monthlyPartialCashOut, investment.currencyFormat)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-blue-600" />
                    </div>
                    <h4 className="font-medium text-blue-800">Patrimonio Final</h4>
                  </div>
                  
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold text-blue-800">
                      <AnimatedNumberDisplay
                        value={showPerCertificate ? perCertificateValues.patrimony : finalPatrimony}
                        currencyFormat={investment.currencyFormat}
                        duration={1.2}
                        staggerDelay={0.08}
                      />
                    </div>
                    <span className="text-sm text-blue-600">{showPerCertificate ? '/certificado' : 'total'}</span>
                  </div>
                  
                  <div className="mt-2">
                    <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                        style={{ width: `${(capitalMultiplier / 10) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showPerCertificate}
                    onChange={() => setShowPerCertificate(!showPerCertificate)}
                    className="form-checkbox h-4 w-4 text-green-600 rounded transition-colors duration-200"
                  />
                  <span className="ml-2 text-sm text-gray-600">Mostrar por Certificado</span>
                </label>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <h4 className="text-lg font-medium text-gray-800">Métricas de Rendimiento</h4>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl shadow-sm transform transition-all duration-300 hover:scale-105">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Award className="w-4 h-4 text-green-600" />
                    </div>
                    <h4 className="text-sm font-medium text-green-700">Multiplicador</h4>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold text-green-800">
                      <AnimatedNumberDisplay
                        value={capitalMultiplier}
                        currencyFormat="MXN"
                        duration={1.0}
                        staggerDelay={0.05}
                        isCurrency={false}
                      />x
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-green-600" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl shadow-sm transform transition-all duration-300 hover:scale-105">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-blue-600" />
                    </div>
                    <h4 className="text-sm font-medium text-blue-700">CAGR</h4>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold text-blue-800">
                      <AnimatedNumberDisplay
                        value={cagr * 100}
                        currencyFormat="MXN"
                        duration={1.0}
                        staggerDelay={0.05}
                        isCurrency={false}
                      />%
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-blue-600" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl shadow-sm transform transition-all duration-300 hover:scale-105">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <Zap className="w-4 h-4 text-purple-600" />
                    </div>
                    <h4 className="text-sm font-medium text-purple-700">TIR</h4>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold text-purple-800">
                      <AnimatedNumberDisplay
                        value={irr * 100}
                        currencyFormat="MXN"
                        duration={1.0}
                        staggerDelay={0.05}
                        isCurrency={false}
                      />%
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-purple-600" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-xl shadow-sm transform transition-all duration-300 hover:scale-105">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                      <Shield className="w-4 h-4 text-amber-600" />
                    </div>
                    <h4 className="text-sm font-medium text-amber-700">Recuperación</h4>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold text-amber-800">
                      {paybackYear ? (
                        <>
                          Año <AnimatedNumberDisplay
                            value={paybackYear}
                            currencyFormat="MXN"
                            duration={1.0}
                            staggerDelay={0.05}
                            isCurrency={false}
                          />
                        </>
                      ) : 'N/A'}
                    </div>
                    {paybackYear && <ArrowDownRight className="w-4 h-4 text-amber-600" />}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Reinvestment Status */}
            {investment.reinvestProfits && (
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-xl shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                    <RefreshCw className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h4 className="font-medium text-indigo-800">Estado de Reinversión</h4>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm text-indigo-600 mb-1">Fondo de Reinversión:</div>
                    <div className="text-xl font-bold text-indigo-800 relative overflow-hidden">
                      <AnimatedNumberDisplay
                        value={lastYearData.reinvestmentFund || 0}
                        currencyFormat={investment.currencyFormat}
                        duration={1.2}
                        staggerDelay={0.08}
                      />
                    </div>
                    <div className="text-xs text-indigo-500 mt-1">
                      Acumulado para próximos certificados
                    </div>
                  </div>
                  
                  {investment.partialCashOut && (
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <div className="text-sm text-amber-600 mb-1">Retiro Parcial ({investment.cashOutPercentage}%):</div>
                      <div className="text-xl font-bold text-amber-800 relative overflow-hidden">
                        <AnimatedNumberDisplay
                          value={monthlyPartialCashOut}
                          currencyFormat={investment.currencyFormat}
                          duration={1.2}
                          staggerDelay={0.08}
                        />
                      </div>
                      <div className="text-xs text-amber-500 mt-1">
                        Mensual / {formatCurrency(monthlyPartialCashOut * 12, investment.currencyFormat)} anual
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Comparison Section */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('comparison')}
          className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white hover:from-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <TrendingUp className="w-6 h-6 text-green-600" />
            <h3 className="font-medium text-gray-800">Comparativa de Inversiones</h3>
          </div>
          {expandedSections.includes('comparison') ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            expandedSections.includes('comparison') ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                  </div>
                  <h4 className="text-sm font-medium text-green-700">Cosecha Capital{patrimonyChartVisibility.showReal ? ' (Real)' : ''}</h4>
                </div>
                <div className="text-2xl font-bold text-green-800 mb-1 relative overflow-hidden">
                  <AnimatedNumberDisplay
                    value={patrimonyChartVisibility.showReal ? results.realFinalPatrimony : finalPatrimony}
                    currencyFormat={investment.currencyFormat}
                    duration={1.4}
                    staggerDelay={0.1}
                  />
                </div>
                <div className="text-sm text-green-600">100% (referencia)</div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Activity className="w-4 h-4 text-blue-600" />
                  </div>
                  <h4 className="text-sm font-medium text-blue-700">CETES{patrimonyChartVisibility.showReal ? ' (Real)' : ''}</h4>
                </div>
                <div className="text-2xl font-bold text-blue-800 mb-1 relative overflow-hidden">
                  <AnimatedNumberDisplay
                    value={patrimonyChartVisibility.showReal ? results.realCetesPatrimony : results.cetesPatrimony}
                    currencyFormat={investment.currencyFormat}
                    duration={1.4}
                    staggerDelay={0.1}
                  />
                </div>
                <div className="text-sm text-blue-600">
                  {formatPercent(patrimonyChartVisibility.showReal ? 
                    (results.realCetesPatrimony / results.realFinalPatrimony) * 100 : 
                    (results.cetesPatrimony / finalPatrimony) * 100)}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-purple-600" />
                  </div>
                  <h4 className="text-sm font-medium text-purple-700">Ahorro Tradicional{patrimonyChartVisibility.showReal ? ' (Real)' : ''}</h4>
                </div>
                <div className="text-2xl font-bold text-purple-800 mb-1 relative overflow-hidden">
                  <AnimatedNumberDisplay
                    value={patrimonyChartVisibility.showReal ? results.realSavingsPatrimony : results.savingsPatrimony}
                    currencyFormat={investment.currencyFormat}
                    duration={1.4}
                    staggerDelay={0.1}
                  />
                </div>
                <div className="text-sm text-purple-600">
                  {formatPercent(patrimonyChartVisibility.showReal ? 
                    (results.realSavingsPatrimony / results.realFinalPatrimony) * 100 : 
                    (results.savingsPatrimony / finalPatrimony) * 100)}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-orange-50 to-red-50 p-4 rounded-xl shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                    <Target className="w-4 h-4 text-orange-600" />
                  </div>
                  <h4 className="text-sm font-medium text-orange-700">Bienes Raíces{patrimonyChartVisibility.showReal ? ' (Real)' : ''}</h4>
                </div>
                <div className="text-2xl font-bold text-orange-800 mb-1 relative overflow-hidden">
                  <AnimatedNumberDisplay
                    value={patrimonyChartVisibility.showReal ? results.realRealEstatePatrimony : results.realEstatePatrimony}
                    currencyFormat={investment.currencyFormat}
                    duration={1.4}
                    staggerDelay={0.1}
                  />
                </div>
                <div className="text-sm text-orange-600">
                  {formatPercent(patrimonyChartVisibility.showReal ? 
                    (results.realRealEstatePatrimony / results.realFinalPatrimony) * 100 : 
                    (results.realEstatePatrimony / finalPatrimony) * 100)}
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-700 mb-2">Notas sobre la Comparación:</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                <li>Todas las comparaciones asumen la misma inversión inicial</li>
                <li>CETES: Rendimiento anual del {investment.cetesRate}%</li>
                <li>Ahorro Tradicional: Rendimiento anual del {investment.savingsRate}%</li>
                <li>Bienes Raíces: Apreciación anual del {investment.realEstateAppreciation}% + Renta del {investment.realEstateRent}%</li>
                {investment.applyTaxes && (
                  <li>Se aplican impuestos del {investment.taxRate}% a los rendimientos de inversiones tradicionales</li>
                )}
                {patrimonyChartVisibility.showReal && (
                  <li>Los valores mostrados están ajustados por inflación (valores reales) para todas las inversiones</li>
                )}
                <li>El valor EBITDA se calcula multiplicando las utilidades anuales por un factor de {investment.ebitdaFactor}x</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsSummary;